import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { useForm } from '@inertiajs/react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';
import type { CalculationRequest } from '../types';

interface EditCalculationSpecSheetProps {
    request: CalculationRequest;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type SpecField = { key: keyof SpecFormValues; labelKey: string; fallback: string };

type SpecFormValues = {
    system: string;
    color_exterior: string;
    color_interior: string;
    glass_g_value: string;
    glass_light_transmittance: string;
    construction_u_value: string;
    infills_enamel: string;
    sound_insulation: string;
    water_tightness_class: string;
    wind_load_resistance: string;
    air_permeability_class: string;
    notes: string;
};

/** Grouped as on the paper form, matching the create form's order. */
const SPEC_GROUPS: Array<{ titleKey: string; fallback: string; fields: SpecField[] }> = [
    {
        titleKey: 'system_and_colours',
        fallback: 'System and colours',
        fields: [
            { key: 'system', labelKey: 'system', fallback: 'System' },
            { key: 'color_exterior', labelKey: 'color_exterior', fallback: 'Colour — exterior' },
            { key: 'color_interior', labelKey: 'color_interior', fallback: 'Colour — interior' },
        ],
    },
    {
        titleKey: 'glazing',
        fallback: 'Glazing',
        fields: [
            { key: 'glass_g_value', labelKey: 'glass_g_value', fallback: 'Solar control (g)' },
            { key: 'glass_light_transmittance', labelKey: 'glass_light_transmittance', fallback: 'Light transmittance (Lt)' },
            { key: 'infills_enamel', labelKey: 'infills_enamel', fallback: 'Infills / enamelled glass' },
        ],
    },
    {
        titleKey: 'performance',
        fallback: 'Performance',
        fields: [
            { key: 'construction_u_value', labelKey: 'construction_u_value', fallback: 'Construction U-value' },
            { key: 'sound_insulation', labelKey: 'sound_insulation', fallback: 'Sound insulation' },
            { key: 'water_tightness_class', labelKey: 'water_tightness_class', fallback: 'Watertightness class' },
            { key: 'wind_load_resistance', labelKey: 'wind_load_resistance', fallback: 'Wind load resistance' },
            { key: 'air_permeability_class', labelKey: 'air_permeability_class', fallback: 'Air permeability class' },
        ],
    },
];

/**
 * Edits the technical specification after the request was raised — what sales
 * wrote down is often refined once engineering looks at it.
 *
 * Only administrators and engineers reach this; the endpoint enforces the same
 * rule, since `calculation-requests.update` is held by sales as well.
 */
export function EditCalculationSpecSheet({ request, open, onOpenChange }: EditCalculationSpecSheetProps) {
    const { t } = useTranslations();

    const { data, setData, patch, processing, errors, reset } = useForm<SpecFormValues>({
        system: request.system ?? '',
        color_exterior: request.color_exterior ?? '',
        color_interior: request.color_interior ?? '',
        glass_g_value: request.glass_g_value ?? '',
        glass_light_transmittance: request.glass_light_transmittance ?? '',
        construction_u_value: request.construction_u_value ?? '',
        infills_enamel: request.infills_enamel ?? '',
        sound_insulation: request.sound_insulation ?? '',
        water_tightness_class: request.water_tightness_class ?? '',
        wind_load_resistance: request.wind_load_resistance ?? '',
        air_permeability_class: request.air_permeability_class ?? '',
        notes: request.notes ?? '',
    });

    // Re-seed on open so a cancelled edit does not linger into the next one.
    useEffect(() => {
        if (!open) {
            return;
        }

        setData({
            system: request.system ?? '',
            color_exterior: request.color_exterior ?? '',
            color_interior: request.color_interior ?? '',
            glass_g_value: request.glass_g_value ?? '',
            glass_light_transmittance: request.glass_light_transmittance ?? '',
            construction_u_value: request.construction_u_value ?? '',
            infills_enamel: request.infills_enamel ?? '',
            sound_insulation: request.sound_insulation ?? '',
            water_tightness_class: request.water_tightness_class ?? '',
            wind_load_resistance: request.wind_load_resistance ?? '',
            air_permeability_class: request.air_permeability_class ?? '',
            notes: request.notes ?? '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, request]);

    const fieldLabel = (key: string, fallback: string) => t(`calculation_requests.form.fields.${key}`, fallback);

    const submit = (event: React.FormEvent) => {
        event.preventDefault();

        patch(route('calculation-requests.update', request.id), {
            preserveScroll: true,
            onSuccess: () => {
                onOpenChange(false);
                reset();
            },
            onError: () => toast.error(t('calculation_requests.show.specification.save_failed', 'Failed to save the specification.')),
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange}>
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('calculation_requests.show.sections.specification', 'Technical specification')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('calculation_requests.show.specification.edit_description', 'Refine what engineering needs to price this request.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-5">
                        {SPEC_GROUPS.map((group) => (
                            <div key={group.titleKey} className="space-y-3">
                                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                                    {t(`calculation_requests.form.spec_groups.${group.titleKey}`, group.fallback)}
                                </p>
                                <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                                    {group.fields.map((field) => (
                                        <div key={field.key} className="space-y-1.5">
                                            <Label htmlFor={`spec-${field.key}`} className="text-xs font-normal">
                                                {fieldLabel(field.labelKey, field.fallback)}
                                            </Label>
                                            <Input
                                                id={`spec-${field.key}`}
                                                value={data[field.key]}
                                                onChange={(event) => setData(field.key, event.target.value)}
                                            />
                                            {errors[field.key] ? <p className="text-destructive text-sm">{errors[field.key]}</p> : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div className="space-y-2 border-t pt-4">
                            <Label htmlFor="spec-notes">{t('calculation_requests.form.fields.notes', 'Notes')}</Label>
                            <Textarea id="spec-notes" rows={4} value={data.notes} onChange={(event) => setData('notes', event.target.value)} />
                            {errors.notes ? <p className="text-destructive text-sm">{errors.notes}</p> : null}
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
